import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { supabase, useAuth } from 'hooks/useAuth';
import { useToast } from 'hooks/useToast';
import { useResponsiveScreenSizes } from 'hooks/useResponsiveScreenSizes';

import { AppScrollProps } from 'components/layout/PursuitContainer';
import { ProfileContainer } from 'components/layout/ProfileContainer';
import { GuidelinesContainer } from 'components/layout/GuidelinesContainer';
import { Button } from 'components/ui/Button';

import { spacing, transparencies } from 'styles/theme';
import {
    Bumper,
    Card,
    FullBleedScroller,
    ProgressBarWrapper,
    ProgressBar,
    CardBottomButtonWrapper,
} from 'styles/shared';

export const PursuitOnboarding = ({ appScrollToTop }: AppScrollProps) => {
    const { userProfile, userId, getProfiles } = useAuth();
    const { showSuccessToast, showErrorToast } = useToast();
    const { isMobileScreenSize } = useResponsiveScreenSizes();

    const [hasAcknowledgedConduct, setHasAcknowledgedConduct] = useState(false);

    const scrollRef = useRef() as React.MutableRefObject<HTMLDivElement>;

    const scrollToTop = useCallback(() => {
        scrollRef.current?.scrollTo({ top: 0 });
    }, []);

    const parsedRawData = useMemo(
        () => JSON.parse(JSON.stringify(userProfile?.raw_data ?? {})),
        [userProfile?.raw_data]
    );

    useEffect(() => {
        appScrollToTop();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (parsedRawData.is_published) {
            scrollToTop();
            appScrollToTop();
        }
        if (hasAcknowledgedConduct) {
            scrollToTop();
            appScrollToTop();
        }
    }, [
        appScrollToTop,
        hasAcknowledgedConduct,
        parsedRawData.is_published,
        scrollToTop,
    ]);

    const progressPercentage = useMemo(() => {
        let percentage = 30;
        if (parsedRawData?.is_published) {
            percentage += 20;
        }
        return percentage;
    }, [parsedRawData?.is_published]);

    const onCompleteOnboarding = useCallback(async () => {
        const { error } = await supabase
            .from('profiles')
            .update({
                raw_data: {
                    ...parsedRawData,
                    has_completed_pursuit_onboarding: true,
                },
            })
            .eq('id', userId);

        if (error == null) {
            showSuccessToast(
                'Pursuit onboarding complete! You now have access to The Pursuit Retreat Schedule and The Pursuit Member Directory.'
            );
            await getProfiles?.();
        } else {
            showErrorToast(error.message);
        }
    }, [getProfiles, parsedRawData, showErrorToast, showSuccessToast, userId]);

    return (
        <FullBleedScroller ref={scrollRef}>
            <Bumper
                $minHeight={
                    isMobileScreenSize ? spacing.xxSmall : spacing.xxxLarge
                }
            />
            <Card
                $width="720px"
                $backgroundColor={transparencies.white95}
                $isMobileOverflowHidden
            >
                <Bumper $minHeight={spacing.xxxSmall} />
                <ProgressBarWrapper>
                    <ProgressBar
                        $width={
                            progressPercentage +
                            (hasAcknowledgedConduct ? 30 : 0)
                        }
                    />
                </ProgressBarWrapper>
                <Bumper $minHeight={spacing.large} />
                {!parsedRawData.is_published ? (
                    <ProfileContainer isProfileCreation />
                ) : !hasAcknowledgedConduct ? (
                    <>
                        <GuidelinesContainer guidelinesTitle="Code of Conduct" />
                        <Bumper $minHeight={spacing.xLarge} />
                        <CardBottomButtonWrapper $backgroundColor="transparent">
                            <Button
                                onClick={() => setHasAcknowledgedConduct(true)}
                            >{`Agree and Accept`}</Button>
                        </CardBottomButtonWrapper>
                    </>
                ) : (
                    <>
                        <GuidelinesContainer guidelinesTitle="How We Show Up" />
                        <Bumper $minHeight={spacing.xLarge} />
                        <CardBottomButtonWrapper $backgroundColor="transparent">
                            <Button
                                onClick={onCompleteOnboarding}
                            >{`Complete Onboarding`}</Button>
                        </CardBottomButtonWrapper>
                    </>
                )}
            </Card>
            <Bumper $minHeight={spacing.xxxLarge} $shouldHideOnMobile />
            <Bumper $minHeight={spacing.xxxLarge} />
        </FullBleedScroller>
    );
};
